<script lang="ts">
  import { onMount } from "svelte"
  import { mdiArrowLeft, mdiCalendarCheck, mdiClose, mdiRefresh } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import {
    fetchAppointmentSlotCalendar,
    searchAppointmentSlots,
    type PublicAppointmentPortalStateResponse,
    type PublicAppointmentSlot,
    type PublicAppointmentSlotCalendarWeek,
  } from "../../../../../../../../api/public/appointment-portal"
  import { formatWeekRangeLabel } from "../../_shared/_logic/appointment-portal"
  import DateSelector from "./_modules/date-selector"
  import SlotSummary from "../../_shared/_modules/slot-summary"
  import TimeSlotList from "../../_shared/_modules/time-slot-list"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"

  const CALENDAR_WEEKS = 12
  const MOBILE_SLOT_BATCH_SIZE = 8

  const padDatePart = (value: number) => value.toString().padStart(2, "0")

  const toDateKey = (date: Date) =>
    `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`

  const startOfToday = () => {
    const now = new Date()

    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }

  const sortCalendarWeeks = (weeks: PublicAppointmentSlotCalendarWeek[]) =>
    [...weeks].sort((left, right) => left.start_date.localeCompare(right.start_date))

  const firstAvailableWeekIndex = (weeks: PublicAppointmentSlotCalendarWeek[]) => {
    const index = weeks.findIndex((week) => week.days.some((day) => day.available))

    return index >= 0 ? index : 0
  }

  const weekIndexForSelectableDate = (
    weeks: PublicAppointmentSlotCalendarWeek[],
    date: string | null,
  ) =>
    date
      ? weeks.findIndex((week) => week.days.some((day) => day.date === date && day.available))
      : -1

  const isAbortError = (err: unknown) =>
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name?: string }).name === "AbortError"

  let {
    orgIdentifier,
    code,
    portalState,
    busy = false,
    onbook,
    onback,
  }: {
    orgIdentifier: string
    code: string
    portalState: PublicAppointmentPortalStateResponse
    busy?: boolean
    onbook: (slot: PublicAppointmentSlot) => Promise<boolean>
    onback: () => void
  } = $props()

  let loadingCalendar = $state(false)
  let loadingSlots = $state(false)
  let calendarError = $state<string | null>(null)
  let slotError = $state<string | null>(null)
  let calendarWeeks = $state<PublicAppointmentSlotCalendarWeek[]>([])
  let slots = $state<PublicAppointmentSlot[]>([])
  let selectedDate = $state<string | null>(null)
  let selectedSlot = $state<PublicAppointmentSlot | null>(null)
  let mobileVisibleSlotCount = $state(MOBILE_SLOT_BATCH_SIZE)
  let selectedWeekIndex = $state(0)
  let calendarRequestId = 0
  let slotRequestId = 0
  let calendarAbortController: AbortController | null = null
  let slotAbortController: AbortController | null = null
  let routePolicyToken = $state<string | null>(null)

  const earliestDateKey = $derived(
    portalState.slot_search_policy?.earliest_date ?? toDateKey(startOfToday()),
  )
  const currentWeek = $derived(calendarWeeks[selectedWeekIndex] ?? null)
  const calendarDays = $derived(currentWeek?.days ?? [])
  const weekLabel = $derived(
    currentWeek ? formatWeekRangeLabel(currentWeek.start_date, currentWeek.end_date) : "",
  )
  const canPreviousWeek = $derived(selectedWeekIndex > 0)
  const canNextWeek = $derived(selectedWeekIndex < calendarWeeks.length - 1)

  const hasExistingAppointment = $derived(Boolean(portalState.appointment))

  const resetMobileSlotReveal = () => {
    mobileVisibleSlotCount = MOBILE_SLOT_BATCH_SIZE
  }

  const loadSlots = async (date: string | null = selectedDate) => {
    slotAbortController?.abort()
    const requestId = ++slotRequestId
    resetMobileSlotReveal()
    slots = []

    if (!date) {
      selectedSlot = null
      loadingSlots = false

      return
    }

    const controller = new AbortController()
    slotAbortController = controller
    loadingSlots = true
    slotError = null
    selectedSlot = null
    try {
      const response = await searchAppointmentSlots(
        orgIdentifier,
        code,
        {
          count: 24,
          date,
          include_flex: true,
          route_policy_token: routePolicyToken,
        },
        { signal: controller.signal },
      )

      if (requestId !== slotRequestId) return

      slots = response.slots
    } catch (err) {
      if (isAbortError(err)) return

      if (requestId !== slotRequestId) return

      console.error("appointment_slot_search_failed", err)
      slots = []
      slotError = (err as { message?: string })?.message ?? "Termine konnten nicht geladen werden."
    } finally {
      if (requestId === slotRequestId) {
        loadingSlots = false

        if (slotAbortController === controller) {
          slotAbortController = null
        }
      }
    }
  }

  const resetSlotSelection = () => {
    selectedDate = null
    slots = []
    selectedSlot = null
    slotError = null
    resetMobileSlotReveal()
    slotAbortController?.abort()
    slotRequestId += 1
    loadingSlots = false
  }

  const loadCalendar = async () => {
    calendarAbortController?.abort()
    const requestId = ++calendarRequestId
    const controller = new AbortController()
    calendarAbortController = controller
    calendarWeeks = []
    selectedWeekIndex = 0
    routePolicyToken = null
    resetSlotSelection()
    loadingCalendar = true
    calendarError = null
    try {
      const response = await fetchAppointmentSlotCalendar(
        orgIdentifier,
        code,
        {
          start_date: earliestDateKey,
          weeks: CALENDAR_WEEKS,
        },
        { signal: controller.signal },
      )

      if (requestId !== calendarRequestId) return

      routePolicyToken = response.route_policy_token ?? null
      calendarWeeks = sortCalendarWeeks(response.weeks)
      selectedWeekIndex = firstAvailableWeekIndex(calendarWeeks)
    } catch (err) {
      if (isAbortError(err)) return

      if (requestId !== calendarRequestId) return

      console.error("appointment_slot_calendar_failed", err)
      calendarError =
        (err as { message?: string })?.message ?? "Terminkalender konnte nicht geladen werden."
    } finally {
      if (requestId === calendarRequestId) {
        loadingCalendar = false

        if (calendarAbortController === controller) {
          calendarAbortController = null
        }
      }
    }
  }

  const selectDate = (date: string) => {
    selectedDate = date
    void loadSlots(date)
  }

  const selectPreviousWeek = () => {
    if (!canPreviousWeek) return

    selectedWeekIndex -= 1
    resetSlotSelection()
  }

  const selectNextWeek = () => {
    if (!canNextWeek) return

    selectedWeekIndex += 1
    resetSlotSelection()
  }

  const retrySelectedDate = () => {
    void loadSlots(selectedDate)
  }

  const showMoreMobileSlots = () => {
    mobileVisibleSlotCount = Math.min(slots.length, mobileVisibleSlotCount + MOBILE_SLOT_BATCH_SIZE)
  }

  const confirmSelectedSlot = async () => {
    if (!selectedSlot) return

    const attemptedDate = selectedDate
    const success = await onbook(selectedSlot)

    if (!success) {
      selectedSlot = null
      await loadCalendar()
      const attemptedWeekIndex = weekIndexForSelectableDate(calendarWeeks, attemptedDate)

      if (attemptedDate && attemptedWeekIndex >= 0) {
        selectedWeekIndex = attemptedWeekIndex
        selectedDate = attemptedDate
        await loadSlots(attemptedDate)
      }
    }
  }

  onMount(() => {
    void loadCalendar()

    return () => {
      calendarAbortController?.abort()
      slotAbortController?.abort()
    }
  })
</script>

<SectionTitle
  title="Passenden Termin auswählen"
  small={true}
  brightBackground={true}
  subtitle={hasExistingAppointment ? "Termin ändern" : "Termin wählen"}
  styleOuter="margin-bottom: 1.2rem;"
>
  {#snippet buttonSnippet()}
    <CTA
      type="tertiaryButton"
      icon={mdiArrowLeft}
      content="Zurück"
      darkMode={false}
      onClick={onback}
    />
  {/snippet}
</SectionTitle>

{#if calendarError && !calendarWeeks.length}
  <div class="empty-state">
    <p>{calendarError}</p>
    <CTA
      type="tertiaryButton"
      icon={mdiRefresh}
      content="Erneut laden"
      darkMode={false}
      onClick={loadCalendar}
    />
  </div>
{:else}
  <div class="selection-grid">
    <div class="date-selector-shell">
      <DateSelector
        days={calendarDays}
        {selectedDate}
        loading={loadingCalendar}
        {weekLabel}
        canPrevious={canPreviousWeek}
        canNext={canNextWeek}
        onprevious={selectPreviousWeek}
        onnext={selectNextWeek}
        onselect={selectDate}
      />
      {#if calendarError}
        <p class="calendar-hint">{calendarError}</p>
      {/if}
    </div>
    <div class="slot-column">
      <div class="slot-list-shell">
        <TimeSlotList
          {slots}
          {selectedSlot}
          dateSelected={Boolean(selectedDate)}
          loading={loadingSlots}
          error={slotError}
          mobileVisibleCount={mobileVisibleSlotCount}
          mobileBatchSize={MOBILE_SLOT_BATCH_SIZE}
          onselect={(slot) => (selectedSlot = slot)}
          onretry={retrySelectedDate}
          onshowmore={showMoreMobileSlots}
        />
      </div>
      <div class="decision-panel">
        <p class="decision-panel-title">Zusammenfassung</p>
        <div class="slot-summary-shell">
          <SlotSummary slot={selectedSlot} />
        </div>
        <div class="actions">
          <span class="action-control action-control--cancel">
            <CTA
              type="tertiaryButton"
              icon={mdiClose}
              content="Abbrechen"
              ariaLabel="Abbrechen"
              darkMode={false}
              onClick={onback}
            />
          </span>
          <span class="action-control action-control--confirm" class:action-control--busy={busy}>
            <CTA
              type="primaryButton"
              icon={mdiCalendarCheck}
              content={busy ? "Wird gebucht" : "Auswahl bestätigen"}
              ariaLabel={busy ? "Wird gebucht" : "Auswahl bestätigen"}
              darkMode={false}
              disabled={!selectedSlot || busy || loadingSlots}
              onClick={confirmSelectedSlot}
              fullWidth={true}
            />
          </span>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .selection-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1.4rem;
    align-items: start;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  .date-selector-shell,
  .slot-column,
  .slot-list-shell,
  .slot-summary-shell,
  .decision-panel {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .slot-column {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(20rem, 1fr);
    gap: 1.25rem;
    align-items: start;
  }

  .slot-list-shell {
    min-height: 0;
  }

  .decision-panel {
    display: grid;
    gap: 0.9rem;
    align-content: start;
  }

  .decision-panel-title {
    margin: 0;
    color: var(--portal-ink, var(--bg-100));
    font-weight: 800;
  }

  .actions {
    align-items: center;
    flex-wrap: nowrap;
  }

  .actions,
  .empty-state {
    display: flex;
    gap: 0.7rem;
    justify-content: flex-end;
  }

  .empty-state {
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    color: var(--portal-muted);
  }

  .actions :global(.cta) {
    min-width: 0;
    padding-inline: 0.85rem;
    font-size: 0.9rem;
  }

  .action-control {
    display: contents;
  }

  .actions :global(.cta.fullWidth) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }

  .calendar-hint {
    margin: 0.65rem 0 0;
    color: var(--portal-muted, #697571);
    font-size: 0.86rem;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    .slot-column {
      grid-template-columns: minmax(0, 1fr);
    }

    .decision-panel-title {
      display: none;
    }
  }

  @media (max-width: 700px) {
    .actions {
      justify-content: stretch;
    }
  }

  @media (max-width: 560px) {
    .action-control--cancel :global(.cta p) {
      display: none;
    }

    .action-control--confirm :global(.cta p) {
      font-size: 0;
    }

    .action-control--confirm :global(.cta p)::after {
      content: "Bestätigen";
      font-size: 0.9rem;
    }

    .action-control--confirm.action-control--busy :global(.cta p)::after {
      content: "Wird gebucht";
    }
  }
</style>
